import { useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Loader from './components/Loader';
import Slider from 'react-slick';
import { eventsRepository } from '../../repositories/EventsRepository';
import { Event, FormatDate, getDistanceBetweenCoordinateAndEvent } from '../../models/Event';
import UserBadge from './components/UserBadge';
import { useConfirm } from '../../hooks/useConfirm';
import { useRouter } from '../../hooks/useRouter';
import { ConnectionButton } from './components/ConnectionButton';
import { plebsRepository } from '../../repositories/PlebsRepository';
import { Pleb } from '../../models/Pleb';
import Clickable from '../../components/Clickable';
import { ChatChannelType } from '../../models/ChatChannel';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import branch from 'branch-sdk';
import { Button, ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { CopyToClipboard } from '../../components/CopyToClipboard';

interface ProfilePageEventProps {}

export const ProfilePageEvent: React.FC<ProfilePageEventProps> = () => {
  const { eventId } = useParams();
  const {pleb} = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [distance, setDistance] = useState<string | null>(null);
  const [organizer, setOrganizer] = useState<Pleb | null>(null);
  const [eventLink, setEventLink] = useState<string | null>(null);
  const navigate = useNavigate();
  const {navigateFromFullChannelId, navigateToProfile} = useRouter();
  const {openConnectDialog} = useConfirm();
  const location = useLocation();

  const handleClickConnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if(event?.organizer) {
      openConnectDialog(event?.organizer?.toJson() ?? {});
    }
  }

  useEffect(() => {
    setEvent(null);

    const fetchEventDetails = async () => {
      try {
        const event = await eventsRepository.getEventDetails(eventId ?? '', pleb?.id ?? '');
        setEvent(event);
        if(event?.organizer)
          delete event.organizer.connection;
        setOrganizer(event?.organizer ?? null);
        if(event && pleb?.coordinates) {
          setDistance(getDistanceBetweenCoordinateAndEvent(pleb.coordinates, event));
        }

        if(event?.organizer) {
          const organizer = await plebsRepository.getUserDetails(event.organizer.id, pleb?.id ?? '');
          setOrganizer(organizer);
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
      }
    };

    fetchEventDetails();
  }, [eventId, pleb?.id, pleb]);

  useEffect(() => {
    if(event) {
        const linkData = {
          data: {
            $og_title: event.name,
            twitter_image_url: event.pictures[0],
            $canonical_identifier: `event/${event.id}`,
            $canonical_url: `https://public.theorangepillapp.com/event/${event.id}`,
            $og_description: event.description,
            id: event.id,
            kind: "event",
            $creation_timestamp: Date.now(),
            $always_deeplink: true,
            $fallback_url: `https://public.theorangepillapp.com/event/${event.id}`,
          },
        };
      
        try {
          branch.link(linkData, (err, link) => {
            if(err) {
              console.error('Error generating Branch link:', err);
            } else {
              setEventLink(link);
            }
          });
        } catch (error) {
          console.error('Error generating Branch link:', error);
        }
    }
  }, [event]);


  const sliderSettings = {
    dots: true,
    infinite: false
  };

  const gmapLink = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent((event?.venue ? event.venue + ', ' : '') + (event?.address ?? ''))

  return (
    <LayoutWithTopBar
      onClickPrevArrow={() => navigate(-1)}
      topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{event?.name}</h1>
        </div>
      }
    >
      {
        event ? (
          <div className="max-w-[440px] w-full mx-auto py-6">
            <div className="rounded-lg bg-brand-bg-5 overflow-hidden" style={{ maxWidth: '100vw' }}>
              <div className="relative">
                <div className="relative">
                  <Slider {...sliderSettings}>
                    {
                      event.pictures.map((picture, index) => (
                        <div key={index}>
                          <img src={picture} alt={event.name} className="w-full aspect-square object-cover" />
                        </div>
                      ))
                    }
                  </Slider>
                  <div className="absolute bottom-4 left-4">
                  <Button 
                    variant={ButtonVariant.primary} 
                    size={ButtonSize.small} 
                    className="gap-1 rounded-xl px-1"
                  >
                    <CopyToClipboard toCopy={eventLink ?? ''} copiedText="Copied">
                      <div className="flex items-center gap-2 px-2">
                        <span className="material-symbols-outlined text-[14px]">ios_share</span>
                        <span>Share</span>
                      </div>
                    </CopyToClipboard>
                  </Button>
                </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">{event.name}</h2>
                  </div>                    
                </div>

                <hr className="my-3" />

                <div className="space-y-4">
                  {
                    distance && (
                      <div className="mb-4 italic">
                        {distance} away
                      </div>
                    )
                  }

                  {event.startDate && (
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-[50px] h-[50px] shrink-0">
                        <i className="text-[42px] material-symbols-outlined">calendar_today</i>
                      </div>
                      <div className="ml-2 flex flex-col">
                        <span className="text-brand-orange">{event.getDateFormatted(FormatDate.date)}</span>
                        <span>{event.getDateFormatted(FormatDate.hour)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-[50px] h-[50px] shrink-0">
                      <i className="text-[42px] material-symbols-outlined">location_on</i>
                    </div>
                    <div className="ml-2 flex flex-col">
                      <a href={gmapLink} target='_blank' rel="noreferrer" className="text-brand-orange">{event.venue}</a>
                      <a className="underline" href={gmapLink} target='_blank' rel="noreferrer">{event.address}</a>
                    </div>
                  </div>

                  {
                    organizer && (
                      <Clickable 
                        className="flex items-center rounded-full"
                        onClick={() => navigateToProfile(ChatChannelType.Messaging, organizer.id, location.pathname)}
                      >
                        <img
                          src={organizer.image}
                          alt="Avatar"
                          className="w-[50px] h-[50px] object-cover rounded-full overflow-hidden bg-white"
                        />
                        
                        <div className="ml-2 mr-2 flex-1 break-words [word-break:break-word]">
                          <span className="text-brand-orange">Organizer</span>
                          <div className="flex items-center">
                            <span className="font-semibold break-words [word-break:break-word]">
                              {organizer.name}
                            </span>
                            
                            {organizer.badge && (
                              <div className="mx-1">
                                <UserBadge badge={organizer.badge} />
                              </div>
                            )}
                          </div>
                        </div>

                        {
                          organizer.connection &&
                            <ConnectionButton   
                              connection={organizer.connection}
                              onConnect={handleClickConnect}
                              onChat={(connectionId) => {
                                navigateFromFullChannelId(connectionId, location.pathname);
                              }}
                            />
                        }
                      </Clickable>
                    )
                  }

                  {event.description && (
                    <div className="prose max-w-none">
                      <div className="flex items-center justify-between gap-2 mt-6 mb-2">
                        <h2 className="text-xl font-bold">About this event</h2>
                        <Button
                          variant={ButtonVariant.inverted}
                          onClick={() => {
                            eventsRepository.rsvpEvent(event.id)
                              .then(() => {
                                navigate(`/home/chat/chat/event/${event.id}`);
                              })
                              .catch((error) => {
                                console.error('Error RSVPing to event:', error);
                              });
                          }}
                          size={ButtonSize.small}
                        >
                          <span className="material-symbols-outlined text-[14px]">chat_bubble</span>
                          Group chat
                        </Button>
                      </div>
                      <div className="prose max-w-none">
                        {event.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Loader />
        )
      }
    </LayoutWithTopBar>
  );
};

export default ProfilePageEvent;